import { z } from '@hono/zod-openapi'
import { zSelectBenefit } from './benefit.zod'

export const zBenefitsWithDetails = zSelectBenefit.extend({
    vendorName: z.string().nullable(),
    vendorLogoUrl: z.string().nullable(),
    categoryName: z.string().nullable(),
    categorySlug: z.string().nullable(),
    benefitTypeName: z.string().nullable().optional(),
    primaryImageUrl: z.string().nullable().optional(),
    applicationsCount: z.number().default(0),
    redemptionsCount: z.number().default(0),
})

export const zBenefitImageInput = z.object({
    id: z.string().optional(),
    benefitId: z.string().optional(),
    url: z.string(),
    fileKey: z.string(),
    altText: z.string().optional().default(''),
    isPrimary: z.boolean().optional().default(false),
    displayOrder: z.coerce.number().optional().default(0),
})

export const zBenefitTierInput = z.object({
    id: z.string().optional(),
    benefitId: z.string().optional(),
    tierName: z.string().optional().nullable(),
    minPurchaseCents: z.coerce.number().min(0).default(0),
    maxPurchaseCents: z.coerce.number().optional().nullable(),
    discountType: z.enum(['percentage', 'flat', 'cashback']),
    flatAmountCents: z.coerce.number().optional().nullable(),
    percentageValue: z.coerce.number().optional().nullable(),
    maxDiscountCents: z.coerce.number().optional().nullable(),
    targetUserLevel: z.string().default('all'),
    priority: z.coerce.number().default(0),
})

export const zBenefitScheduleInput = z.object({
    id: z.string().optional(),
    benefitId: z.string().optional(),
    scheduleType: z.enum([
        'campaign',
        'blackout',
        'peak_season',
        'day_of_week',
    ]),
    startDate: z.string(),
    endDate: z.string(),
    dayOfWeek: z.coerce.number().min(0).max(6).optional().nullable(),
    isBlocked: z.boolean().default(false),
    discountMultiplier: z.coerce.number().optional().default(1),
    discountOverridePercent: z.coerce.number().optional().nullable(),
    description: z.string().optional().nullable(),
})

export const zBenefitWithDetails = zBenefitsWithDetails.extend({
    vendorDescription: z.string().nullable(),
    vendorContactEmail: z.string().nullable(),
    vendorContactPhone: z.string().nullable(),
    categoryDescription: z.string().nullable(),
    targetClients: z
        .array(
            z.object({
                clientId: z.string(),
                clientName: z.string(),
                accessType: z.enum(['allowed', 'blocked']),
            }),
        )
        .optional(),
    tiers: z.array(zBenefitTierInput).optional(),
    schedules: z.array(zBenefitScheduleInput).optional(),
    images: z.array(zBenefitImageInput).optional(),
})

export const zCalendarDayInput = z.object({
    date: z.string(),
    status: z.enum(['available', 'not-redeemable', 'peak-season']),
})

export const zCreateBenefit = z.object({
    title: z.string().min(1, 'Title is required'),
    shortDescription: z.string().nullable().optional(),
    fullDescription: z.string().nullable().optional(),
    termsConditions: z.string().nullable().optional(),
    serviceProviderCategoryId: z.string().min(1, 'Category is required'),
    benefitTypeId: z.string().optional(),
    offerType: z.enum([
        'percentage',
        'flat',
        'cashback',
        'raffle',
        'upgrade',
        'group',
    ]),
    discountPercent: z.coerce.number().nullable().optional(),
    maxDiscount: z.coerce.number().nullable().optional(),
    minPurchase: z.coerce.number().nullable().optional(),
    discountAmount: z.coerce.number().nullable().optional(),
    cashbackAmount: z.coerce.number().nullable().optional(),
    maxCashback: z.coerce.number().nullable().optional(),
    numberOfWinners: z.coerce.number().nullable().optional(),
    prizeDescription: z.string().nullable().optional(),
    validFrom: z.string().min(1, 'Valid from date is required'),
    validUntil: z.string().min(1, 'Valid until date is required'),
    availabilityMode: z
        .enum(['platform', 'specific', 'excluding'])
        .default('platform'),
    selectedCompanies: z.array(z.string()).optional().default([]),
    excludedCompanies: z.array(z.string()).optional().default([]),
    targetClientIds: z.array(z.string()).optional().default([]),
    calendarDays: z.array(zCalendarDayInput).optional().default([]),
    maxTotalRedemptions: z.coerce.number().optional().nullable(),
    maxApplicationsPerEmployee: z.coerce.number().default(1),
    status: z.enum(['draft', 'published']).default('published'),
    vendorId: z.string().optional(),

    // Dynamic tiered volume & scheduling rules
    tiers: z.array(zBenefitTierInput).optional().default([]),
    schedules: z.array(zBenefitScheduleInput).optional().default([]),
    images: z.array(zBenefitImageInput).optional().default([]),
})

export const zClientOption = z.object({
    id: z.string(),
    name: z.string(),
    logoUrl: z.string().nullable(),
})

export type BenefitImageInput = z.infer<typeof zBenefitImageInput>
export type BenefitTierInput = z.infer<typeof zBenefitTierInput>
export type BenefitScheduleInput = z.infer<typeof zBenefitScheduleInput>
export type CreateBenefit = z.infer<typeof zCreateBenefit>
export const zCreateBenefitComposite = zCreateBenefit
export type CreateBenefitComposite = CreateBenefit
export type CalendarDayInput = z.infer<typeof zCalendarDayInput>
export type ClientOption = z.infer<typeof zClientOption>
export type BenefitWithRelations = z.infer<typeof zBenefitsWithDetails>
export type BenefitDetailWithRelations = z.infer<typeof zBenefitWithDetails>
export const zUpdateBenefitStatus = z.object({
    status: z.enum(['draft', 'published', 'expired', 'rejected']),
})
export type UpdateBenefitStatus = z.infer<typeof zUpdateBenefitStatus>

export const zMultipartCreateBenefit = z.object({
    data: z.string().openapi({
        description: 'JSON stringified CreateBenefit payload',
    }),
    images: z
        .any()
        .openapi({
            type: 'array',
            items: { type: 'string', format: 'binary' },
            description: 'Optional array of image files',
        })
        .optional(),
    imagesMetadata: z
        .string()
        .openapi({
            description: 'JSON stringified array of BenefitImageMetadataInput',
        })
        .optional(),
})

export type MultipartCreateBenefit = z.infer<typeof zMultipartCreateBenefit>

export const zUpdateBenefitImageMultipart = z.object({
    file: z
        .any()
        .openapi({
            type: 'string',
            format: 'binary',
            description: 'New image file to replace in storage',
        })
        .optional(),
    altText: z.string().optional(),
    isPrimary: z.string().optional(),
    displayOrder: z.string().optional(),
})

export type UpdateBenefitImageMultipart = z.infer<
    typeof zUpdateBenefitImageMultipart
>

export const zUpdateBenefitImageJson = z.object({
    altText: z.string().optional(),
    isPrimary: z.boolean().optional(),
    displayOrder: z.number().int().optional(),
})

export type UpdateBenefitImageJson = z.infer<typeof zUpdateBenefitImageJson>

export const zUpdateBenefitComposite = zCreateBenefit.partial()
export type UpdateBenefitComposite = z.infer<typeof zUpdateBenefitComposite>

export const zMultipartUpdateBenefit = z.object({
    data: z.string().optional().openapi({
        description: 'JSON stringified UpdateBenefitComposite payload',
    }),
    images: z
        .any()
        .openapi({
            type: 'array',
            items: { type: 'string', format: 'binary' },
            description: 'Optional array of image files',
        })
        .optional(),
    imagesMetadata: z
        .string()
        .openapi({
            description: 'JSON stringified array of BenefitImageMetadataInput',
        })
        .optional(),
})

export type MultipartUpdateBenefit = z.infer<typeof zMultipartUpdateBenefit>
